import {
  Button,
  Checkbox,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconInfoCircle, IconLock } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import classes from './Compliance.module.css';
import { confirmableItems, replaceable } from './certificate-view.js';
import { useCertificateErrorText } from './errors.js';
import { ScanInput, useErrorOf, useSchemaForm } from './form-parts.js';
import { confirmationFormSchema, endorsementFormSchema, issuanceFormSchema } from './forms.js';
import type {
  CertificateListItem,
  CertificateType,
  ClassDueRow,
  ConfirmationInput,
  EndorsementInput,
  IssuanceInput,
} from './types.js';
import { ENDORSEMENT_KINDS } from './types.js';

/** Değiştirilemez kayıt diyaloğunun çerçevesi: başlık, "kaydedilince değişmez" notu, hata. */
function ShipRecordModal({
  opened,
  title,
  onClose,
  error,
  children,
  submitLabel,
  submitting,
  disabled,
  onSubmit,
}: {
  opened: boolean;
  title: string;
  onClose: () => void;
  error: unknown;
  children: ReactNode;
  submitLabel: string;
  submitting: boolean;
  disabled?: boolean;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useCertificateErrorText();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={<span className={classes.drawerTitle}>{title}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <Stack gap="sm">
          {children}
          <p className={classes.notice}>
            <IconLock size={16} aria-hidden />
            {t('vesselCertificates.dialogs.immutable')}
          </p>
        </Stack>
        {error ? (
          <p className={classes.formError} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('vesselCertificates.dialogs.cancel')}
          </Button>
          <Button type="submit" loading={submitting} disabled={disabled === true}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------- Vize ---------- */

export interface EndorsementValues {
  surveyKind: string;
  endorsedOn: string | null;
  place: string;
  surveyor: string;
  remarks: string;
  [key: string]: unknown;
}

export function emptyEndorsement(today: string): EndorsementValues {
  return { surveyKind: 'annual', endorsedOn: today, place: '', surveyor: '', remarks: '' };
}

export interface EndorsementDialogProps {
  opened: boolean;
  certificate: Pick<CertificateListItem, 'id' | 'name' | 'number'> | null;
  /** Gemi günü: vize günü gelecekte olamaz. */
  today: string;
  onClose: () => void;
  onSubmit: (input: EndorsementInput, scan: File | null) => Promise<void>;
}

/**
 * "Record Endorsement" (gemi; K28): sörveyörün yıllık, ara ya da ek denetim vizesi. Değiştirilemez;
 * pencere dışı vize kaydedilir ama karşılamaz (ADR 0029 D5).
 */
export function EndorsementDialog({
  opened,
  certificate,
  today,
  onClose,
  onSubmit,
}: EndorsementDialogProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.dialogs.endorsement.${key}`);
  const schema = endorsementFormSchema(today);
  const form = useSchemaForm<EndorsementValues>(schema, emptyEndorsement(today));
  const errorOf = useErrorOf();
  const [scan, setScan] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened) return;
    form.reset(emptyEndorsement(today));
    setScan(null);
    setError(null);
  }, [opened, today, form]);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(schema.parse(values) as EndorsementInput, scan);
    } catch (failure) {
      setError(failure);
    }
  });
  return (
    <ShipRecordModal
      opened={opened}
      title={k('title')}
      onClose={onClose}
      error={error}
      submitLabel={k('save')}
      submitting={isSubmitting}
      onSubmit={() => void submit()}
    >
      <TextInput
        label={k('certificate')}
        value={certificate ? `${certificate.name} · ${certificate.number}` : ''}
        readOnly
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="surveyKind"
          render={({ field }) => (
            <Select
              label={k('kind')}
              required
              data={ENDORSEMENT_KINDS.map((value) => ({ value, label: k(`kinds.${value}`) }))}
              value={field.value}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ?? 'annual')}
              error={errorOf(errors, 'surveyKind')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="endorsedOn"
          render={({ field }) => (
            <DayInput
              label={k('endorsedOn')}
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={today}
              error={errorOf(errors, 'endorsedOn')}
            />
          )}
        />
        <TextInput
          label={k('place')}
          required
          {...form.register('place')}
          error={errorOf(errors, 'place')}
        />
        <TextInput
          label={k('surveyor')}
          required
          {...form.register('surveyor')}
          error={errorOf(errors, 'surveyor')}
        />
      </SimpleGrid>
      <Textarea
        label={k('remarks')}
        autosize
        minRows={2}
        {...form.register('remarks')}
        error={errorOf(errors, 'remarks')}
      />
      <ScanInput label={k('scan')} value={scan} onChange={setScan} />
    </ShipRecordModal>
  );
}

/* ---------- Sörveyör teyidi ---------- */

export interface ConfirmationValues {
  surveyor: string;
  confirmedOn: string | null;
  items: { classItemCode: string; workRecordId: string }[];
  remarks: string;
  [key: string]: unknown;
}

export interface ConfirmationDialogProps {
  opened: boolean;
  rows: readonly ClassDueRow[];
  today: string;
  onClose: () => void;
  onSubmit: (input: ConfirmationInput, scan: File | null) => Promise<void>;
}

/**
 * "Record Surveyor Confirmation" (gemi; K31; ADR 0029 D10): klas için sayılmış ve teyit edilmemiş
 * başmühendis sörveyleri; sörveyör, gün ve imzalı rapor taraması. Değiştirilemez.
 */
export function ConfirmationDialog({
  opened,
  rows,
  today,
  onClose,
  onSubmit,
}: ConfirmationDialogProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.dialogs.confirmation.${key}`, values);
  const schema = confirmationFormSchema(today);
  const initial: ConfirmationValues = { surveyor: '', confirmedOn: today, items: [], remarks: '' };
  const form = useSchemaForm<ConfirmationValues>(schema, initial);
  const errorOf = useErrorOf();
  const [scan, setScan] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { errors, isSubmitting, isDirty } = form.formState;
  const open = confirmableItems(rows);
  useUnsavedChanges(opened && isDirty);
  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta bir kez sıfırlanır
  useEffect(() => {
    if (!opened) return;
    form.reset({ surveyor: '', confirmedOn: today, items: [], remarks: '' });
    setScan(null);
    setError(null);
  }, [opened, today]);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(schema.parse(values) as ConfirmationInput, scan);
    } catch (failure) {
      setError(failure);
    }
  });
  const itemsError = errorOf(errors as never, 'items');
  return (
    <ShipRecordModal
      opened={opened}
      title={k('title')}
      onClose={onClose}
      error={error}
      submitLabel={k('save')}
      submitting={isSubmitting}
      disabled={open.length === 0}
      onSubmit={() => void submit()}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label={k('surveyor')}
          required
          {...form.register('surveyor')}
          error={errorOf(errors, 'surveyor')}
        />
        <Controller
          control={form.control}
          name="confirmedOn"
          render={({ field }) => (
            <DayInput
              label={k('confirmedOn')}
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={today}
              error={errorOf(errors, 'confirmedOn')}
            />
          )}
        />
      </SimpleGrid>
      {open.length === 0 ? (
        <p className={classes.notice}>
          <IconInfoCircle size={16} aria-hidden />
          {k('noItems')}
        </p>
      ) : (
        <Controller
          control={form.control}
          name="items"
          render={({ field }) => (
            <Checkbox.Group
              label={k('items')}
              required
              value={field.value.map((i) => `${i.classItemCode}|${i.workRecordId}`)}
              onChange={(values) =>
                field.onChange(
                  values.map((value) => {
                    const [classItemCode = '', workRecordId = ''] = value.split('|');
                    return { classItemCode, workRecordId };
                  }),
                )
              }
              error={itemsError}
            >
              <Stack gap={6} mt={6}>
                {open.map((row) => (
                  <Checkbox
                    key={row.itemId}
                    value={`${row.itemCode}|${row.lastCeSurvey?.workRecordId ?? ''}`}
                    label={k('itemOption', {
                      code: row.itemCode,
                      description: row.description,
                      date: row.lastCeSurvey?.doneOn ?? '',
                    })}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
          )}
        />
      )}
      <Textarea label={k('remarks')} autosize minRows={2} {...form.register('remarks')} />
      <ScanInput label={k('scan')} value={scan} onChange={setScan} />
    </ShipRecordModal>
  );
}

/* ---------- Gemide düzenlenen sertifika ---------- */

export interface IssuanceValues {
  typeId: string | null;
  replacesCertificateId: string | null;
  number: string;
  issuedBy: string;
  issueDate: string | null;
  expiryDate: string | null;
  shortTerm: boolean;
  remarks: string;
  [key: string]: unknown;
}

export function emptyIssuance(today: string, typeId: string | null = null): IssuanceValues {
  return {
    typeId,
    replacesCertificateId: null,
    number: '',
    issuedBy: '',
    issueDate: today,
    expiryDate: null,
    shortTerm: false,
    remarks: '',
  };
}

export interface IssuanceDialogProps {
  opened: boolean;
  types: readonly CertificateType[];
  items: readonly CertificateListItem[];
  /** Ağaçta seçili tür (önerilen). */
  typeId: string | null;
  today: string;
  onClose: () => void;
  onSubmit: (input: IssuanceInput, scan: File | null) => Promise<void>;
}

/**
 * "Record On-board Issuance" (gemi; K28; ADR 0029 D6): sörveyörün gemide düzenlediği yeni ya da
 * kısa süreli sertifika; ofis kaydı gelene kadar geçerli sayılır, yerine geçtiği geçmişte kalır.
 */
export function IssuanceDialog({
  opened,
  types,
  items,
  typeId,
  today,
  onClose,
  onSubmit,
}: IssuanceDialogProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.dialogs.issuance.${key}`);
  const schema = issuanceFormSchema(today);
  const form = useSchemaForm<IssuanceValues>(schema, emptyIssuance(today, typeId));
  const errorOf = useErrorOf();
  const [scan, setScan] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta bir kez sıfırlanır
  useEffect(() => {
    if (!opened) return;
    form.reset(emptyIssuance(today, typeId));
    setScan(null);
    setError(null);
  }, [opened, today, typeId]);
  const chosenType = form.watch('typeId');
  const candidates = replaceable(items, chosenType);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(schema.parse(values) as IssuanceInput, scan);
    } catch (failure) {
      setError(failure);
    }
  });
  const sorted = [...types].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <ShipRecordModal
      opened={opened}
      title={k('title')}
      onClose={onClose}
      error={error}
      submitLabel={k('save')}
      submitting={isSubmitting}
      onSubmit={() => void submit()}
    >
      <p className={classes.notice}>
        <IconInfoCircle size={16} aria-hidden />
        {k('hint')}
      </p>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="typeId"
          render={({ field }) => (
            <Select
              label={k('type')}
              required
              searchable
              data={sorted.map((type) => ({ value: type.id, label: type.name }))}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                form.setValue('replacesCertificateId', null);
              }}
              error={errorOf(errors, 'typeId')}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
        <Controller
          control={form.control}
          name="replacesCertificateId"
          render={({ field }) => (
            <Select
              label={k('replaces')}
              data={[
                { value: '', label: k('replacesNone') },
                ...candidates.map((item) => ({
                  value: item.id,
                  label: `${item.name} · ${item.number}`,
                })),
              ]}
              value={field.value ?? ''}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ? value : null)}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
        <TextInput
          label={k('number')}
          required
          {...form.register('number')}
          error={errorOf(errors, 'number')}
        />
        <TextInput
          label={k('issuedBy')}
          required
          {...form.register('issuedBy')}
          error={errorOf(errors, 'issuedBy')}
        />
        <Controller
          control={form.control}
          name="issueDate"
          render={({ field }) => (
            <DayInput
              label={k('issueDate')}
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={today}
              error={errorOf(errors, 'issueDate')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="expiryDate"
          render={({ field }) => (
            <DayInput
              label={k('expiryDate')}
              required
              value={field.value}
              onChange={field.onChange}
              error={errorOf(errors, 'expiryDate')}
            />
          )}
        />
      </SimpleGrid>
      <Checkbox label={k('shortTerm')} {...form.register('shortTerm')} />
      <Textarea label={k('remarks')} autosize minRows={2} {...form.register('remarks')} />
      <ScanInput label={k('scan')} value={scan} onChange={setScan} />
    </ShipRecordModal>
  );
}
